import axios from "axios";
import { getSellerOrgApiCode } from "../config/sellerOrgConfig";
import { dispatchResetLoader, dispatchStartLoading, dispatchStopLoading } from "../Redux/Utils/loaderDispatcher";
import { notifyError } from "../components/ToastContainer/Toast/Toast";

/** App version from env (e.g. set in CI from package.json). Fallback for local dev. */
const APP_VERSION = process.env.REACT_APP_APP_VERSION ?? "1.0.1";
/** Version code / build number from env (e.g. CI build id). */
const VERSION_CODE = process.env.REACT_APP_VERSION_CODE ?? "1";
const BLOCKED_ACCOUNT_MESSAGE = "Your ID is blocked by admin";

// Request deduplication map to prevent duplicate submissions
const pendingRequests = new Map<string, Promise<any>>();

/**
 * Two different requests to the same endpoint are not duplicates.
 *
 * Keying on method+url alone collapsed them: refunding an invoice as "normal"
 * and then as "instant" within the same tick returned the first call's result
 * for both, so the second silently did nothing. The body is part of what makes
 * a request unique, so it is part of the key.
 */
const getRequestKey = (method: string, url: string, data?: any): string => {
  let body = "";
  try {
    body = data === undefined ? "" : JSON.stringify(data);
  } catch {
    // Circular or non-serialisable (FormData, File): treat as always unique.
    body = String(Date.now());
  }
  return `${method}:${url}:${body}`;
};
const getApiMessage = (error: any): string =>
  error?.response?.data?.message || error?.message || "";

// ── Refresh-token mutex ──────────────────────────────────────────────
// When multiple requests hit 401 at the same time, only ONE refresh call
// is made. All others wait for the same promise and then retry with the
// new access token.
let isRefreshing = false;
let refreshSubscribers: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

const subscribeTokenRefresh = (resolve: (token: string) => void, reject: (err: any) => void) => {
  refreshSubscribers.push({ resolve, reject });
};

const onTokenRefreshed = (newToken: string) => {
  refreshSubscribers.forEach((sub) => sub.resolve(newToken));
  refreshSubscribers = [];
};

const onRefreshFailed = (err: any) => {
  refreshSubscribers.forEach((sub) => sub.reject(err));
  refreshSubscribers = [];
};

/**
 * Calls the refresh-token endpoint using a **separate** axios instance
 * so that the request does NOT go through ApiClient's interceptors
 * (which would cause infinite 401 → refresh → 401 loops).
 */
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }
  const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
  const res = await axios.post(
    `${baseUrl}/auth/refresh-token`,
    { refresh_token: refreshToken },
    {
      headers: {
        "Content-Type": "application/json",
        "Org-Code": getSellerOrgApiCode(),
        "X-Device-Type": "web",
      },
    }
  );
  const newAccessToken = res.data?.data?.accessToken;
  const newRefreshToken = res.data?.data?.refreshToken || res.data?.data?.refresh_token;
  if (!newAccessToken) {
    throw new Error("No access token in refresh response");
  }
  // Store the new refresh token so the session extends another 7 days
  if (newRefreshToken) {
    localStorage.setItem("refresh_token", newRefreshToken);
  }
  return newAccessToken;
};

// HTTP methods that are safe to auto-retry on network/timeout errors.
// Non-idempotent methods (POST, PATCH, DELETE) are intentionally excluded
// because retrying them can produce duplicate rows if the server is merely
// slow rather than actually failing (e.g. the long store-creation flow).
const RETRY_SAFE_METHODS = new Set(["get", "head", "options"]);

// Transient upstream failures worth retrying on idempotent reads. The Nest
// services sit behind a proxy that answers 502/503/504 while a container is
// restarting, and 429 while a burst of dashboard polls is being shed — all of
// which surfaced to the user as a bare "network error".
const RETRY_SAFE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

const MAX_RETRIES = 3;

/** Honour Retry-After when the server sends one, otherwise back off exponentially. */
const getRetryDelay = (error: any, attempt: number): number => {
  const header = error?.response?.headers?.["retry-after"];
  if (header) {
    const seconds = Number(header);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.min(seconds * 1000, 10000);
    }
  }
  // 1s, 2s, 4s — plus jitter, so the three dashboard tabs polling on the same
  // 15s timer do not all retry on the identical millisecond.
  const base = Math.pow(2, attempt) * 500;
  return base + Math.floor(Math.random() * 250);
};

// Create Axios instance
const ApiClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "application/json",
    "Org-Code": getSellerOrgApiCode(),
    // "X-App-Version": APP_VERSION,
    // "X-Version-Code": VERSION_CODE,
    "X-Device-Type": "web",
  },
  timeout: 60000, // 60 seconds timeout for slow network/large uploads
});

// AbortController: cancel in-flight requests when user navigates away
let currentAbortController = new AbortController();

const getRequestSignal = () => currentAbortController.signal;

export const abortInFlightRequests = () => {
  currentAbortController.abort();
  currentAbortController = new AbortController();
};

// Request Interceptor
ApiClient.interceptors.request.use(
  (config) => {
    // Allow per-call AbortSignal (e.g. hook-scoped cancellation) without cancelling unrelated requests.
    if (!config.signal) {
      config.signal = getRequestSignal();
    }
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Only log in development mode to avoid performance overhead
    if (process.env.NODE_ENV === "development") {
      console.log("Request Sent:", config.method?.toUpperCase(), config.url);
    }
    return config;
  },
  (error) => {
    console.error("Request Error:", error);
    return Promise.reject(error);
  }
);

// Response Interceptor with retry logic
ApiClient.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === "development") {
      console.log("Response Received:", response.status, response.config.url);
    }
    // Don't stop loading here - let handleApiCall manage it
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    
    const originalRequest = error.config;

    // A failure raised before a request was ever built (e.g. a request
    // interceptor throwing) has no config. Reject it as-is rather than
    // throwing a TypeError inside the error handler, which would hide the
    // original cause completely.
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Track retry attempts
    if (!originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }

    // Retry logic for timeout / network errors.
    // IMPORTANT: Only retry idempotent HTTP methods. Auto-retrying a POST
    // (e.g. /stores create) on timeout can create duplicate rows when the
    // server is simply slow — the first request may still be processing
    // and succeed even though axios gave up on the connection.
    const isTimeoutError = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
    // axios v1 reports a dropped connection as ERR_NETWORK; older builds only
    // set the message. Match both so a real outage is not mistaken for a 4xx.
    const isNetworkError =
      error.code === 'ERR_NETWORK' || (error.message === 'Network Error' && !error.response);
    const isTransientStatus =
      !!error.response && RETRY_SAFE_STATUSES.has(error.response.status);
    const method = (originalRequest.method || '').toLowerCase();
    const isRetryableMethod = RETRY_SAFE_METHODS.has(method);

    // A timeout has already cost the full 60s window, so retry it once at
    // most. Network drops and gateway errors fail in milliseconds, so those
    // get the full budget — three cheap attempts ride out a container restart.
    const retryBudget = isTimeoutError ? 1 : MAX_RETRIES;

    if (
      (isTimeoutError || isNetworkError || isTransientStatus) &&
      isRetryableMethod &&
      originalRequest._retryCount < retryBudget
    ) {
      originalRequest._retryCount += 1;
      const delay = getRetryDelay(error, originalRequest._retryCount);

      if (process.env.NODE_ENV === "development") {
        console.warn(
          `Retrying ${method.toUpperCase()} ${originalRequest.url} (attempt ${originalRequest._retryCount}/${retryBudget}) after ${delay}ms:`,
          error.code || error.response?.status
        );
      }

      await new Promise(resolve => setTimeout(resolve, delay));
      return ApiClient(originalRequest);
    }

    // Retries are exhausted (or this failure was never retryable). Carry the
    // attempt count on the error so the UI can tell the user what was already
    // tried instead of showing a bare "network error".
    error.retryAttempts = originalRequest._retryCount;

    // For non-idempotent methods (POST/PATCH/DELETE) we surface the error
    // to the caller without retrying. The UI is responsible for messaging
    // and letting the user decide whether to resubmit.

    // ── 401 handling with refresh-token mutex ──────────────────────
    // Only attempt refresh once per request. The _isRefreshRetry flag
    // prevents infinite loops if the retried request also returns 401.
    if (
      error.response?.status === 401 &&
      getApiMessage(error) === BLOCKED_ACCOUNT_MESSAGE
    ) {
      notifyError(BLOCKED_ACCOUNT_MESSAGE);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("LOGIN_STATUS");
      localStorage.setItem("LOGOUT_STATUS", "LOGOUT");
      dispatchResetLoader();
      dispatchStopLoading();
      try {
        localStorage.removeItem("persist:root");
      } catch {
        // ignore
      }
      window.location.href = "/";
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._isRefreshRetry) {
      originalRequest._isRefreshRetry = true;

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            (newToken: string) => {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              resolve(ApiClient(originalRequest));
            },
            (err: any) => {
              reject(err);
            }
          );
        });
      }

      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        localStorage.setItem("access_token", newAccessToken);

        // Reset flag BEFORE notifying subscribers to avoid race condition
        // where retried requests see isRefreshing=true and queue forever
        isRefreshing = false;

        // Notify all queued requests — they will retry with the new token
        onTokenRefreshed(newAccessToken);

        // Retry original request
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        if (process.env.NODE_ENV === "development") {
          console.log("Retrying request with refreshed token:", originalRequest.url);
        }
        return ApiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshFailed(refreshError);
        console.error("Token refresh failed:", refreshError);
        notifyError(
          getApiMessage(refreshError) === BLOCKED_ACCOUNT_MESSAGE
            ? BLOCKED_ACCOUNT_MESSAGE
            : "Session expired. Please log in again."
        );
        // Clean only auth keys — not localStorage.clear() which nukes
        // other tabs' state and causes cascading logouts.
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("LOGIN_STATUS");
        localStorage.setItem("LOGOUT_STATUS", "LOGOUT");
        // Ensure loader is not stuck while login page initializes.
        dispatchResetLoader();
        dispatchStopLoading();

        // Prevent stale redux-persist state from rehydrating "loading" UI.
        // This is the fastest way to stop the "login keeps loading for a few seconds" issue.
        try {
          localStorage.removeItem("persist:root");
        } catch {
          // ignore (storage may be unavailable)
        }

        // Kick off full logout cleanup (FCM/service worker cleanup) in background.
        // We intentionally do NOT await to avoid extra delay before redirecting to login.
        void import("../utils/logoutFN")
          .then(({ logout }) => {
            try {
              void logout();
            } catch {
              // ignore
            }
          })
          .catch(() => {
            // ignore - redirect still happens below
          });

        window.location.href = "/";
        return Promise.reject(refreshError);
      }
    }

    // Log errors only in development
    if (process.env.NODE_ENV === "development") {
      console.error(`Error in API call (${originalRequest.method?.toUpperCase()} ${originalRequest.url}):`, error.message);
    }

    // Don't stop loading here - let handleApiCall manage it
    return Promise.reject(error);
  }
);

// Main API call handler
export const handleApiCall = async (method, url, data = null, config = {}, isLoading = true) => {
  let loadingStarted = false;
  const skipDeduplication = Boolean((config as any)?.skipDeduplication);
  const axiosConfig =
    config && typeof config === "object"
      ? Object.fromEntries(
          Object.entries(config).filter(([key]) => key !== "skipDeduplication")
        )
      : config;
  
  // Request deduplication: prevent duplicate POST/PATCH/DELETE requests
  const requestKey = getRequestKey(method, url, data);
  if (
    !skipDeduplication &&
    ['post', 'patch', 'put', 'delete'].includes(method.toLowerCase()) &&
    pendingRequests.has(requestKey)
  ) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`Duplicate request prevented: ${method.toUpperCase()} ${url}`);
    }
    return pendingRequests.get(requestKey)!;
  }

  // UI watchdog must align with the axios timeout. If the caller overrode
  // `timeout` per-request (e.g. slow endpoints like POST /stores), honour
  // that value so the spinner does not vanish while the request is still
  // in flight. Falls back to the ApiClient instance default (60s).
  const watchdogMs =
    (axiosConfig as any)?.timeout && typeof (axiosConfig as any).timeout === "number"
      ? (axiosConfig as any).timeout
      : 60000;
  const timeoutId = isLoading ? setTimeout(() => {
    console.warn(`API request stuck for ${Math.round(watchdogMs / 1000)} seconds, resetting loader state:`, url);
    dispatchStopLoading();
  }, watchdogMs) : null;

  const apiCallPromise = (async () => {
    try {
      if (isLoading) {
        dispatchStartLoading();
        loadingStarted = true;
      }
      const response = await ApiClient[method](url, data, axiosConfig);
      if (loadingStarted) {
        dispatchStopLoading();
      }
      if (timeoutId) clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (loadingStarted) {
        dispatchStopLoading();
      }
      if (timeoutId) clearTimeout(timeoutId);
      if (!axios.isCancel(error)) {
        if (process.env.NODE_ENV === "development") {
          console.error(`Error in API call (${method.toUpperCase()} ${url}):`, error instanceof Error ? error.message : error);
        }
      }
      throw error;
    } finally {
      // Clean up pending request after completion
      if (!skipDeduplication && ['post', 'patch', 'put', 'delete'].includes(method.toLowerCase())) {
        pendingRequests.delete(requestKey);
      }
    }
  })();

  // Store pending request for deduplication
  if (!skipDeduplication && ['post', 'patch', 'put', 'delete'].includes(method.toLowerCase())) {
    pendingRequests.set(requestKey, apiCallPromise);
  }

  return apiCallPromise;
};

/**
 * API call handler with upload progress tracking (for large file uploads)
 * @param method - HTTP method (post, patch, etc.)
 * @param url - API endpoint URL
 * @param data - Request data (FormData recommended for file uploads)
 * @param onProgress - Callback function receiving progress: (progressEvent) => void
 * @param config - Additional axios config
 * @param isLoading - Show loading indicator
 * @returns Promise with API response
 */
export const handleApiCallWithProgress = async (
  method: string,
  url: string,
  data: any,
  onProgress: (progress: number) => void,
  config = {},
  isLoading = true
) => {
  let loadingStarted = false;
  // Align watchdog with per-request axios timeout (see handleApiCall above).
  const watchdogMs =
    (config as any)?.timeout && typeof (config as any).timeout === "number"
      ? (config as any).timeout
      : 60000;
  const timeoutId = isLoading ? setTimeout(() => {
    console.warn(`API request stuck for ${Math.round(watchdogMs / 1000)} seconds, resetting loader state:`, url);
    dispatchStopLoading();
  }, watchdogMs) : null;

  try {
    if (isLoading) {
      dispatchStartLoading();
      loadingStarted = true;
    }

    const uploadConfig = {
      ...config,
      onUploadProgress: (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      },
    };

    const response = await ApiClient[method](url, data, uploadConfig);
    if (loadingStarted) {
      dispatchStopLoading();
    }
    if (timeoutId) clearTimeout(timeoutId);
    return response;
  } catch (error) {
    if (loadingStarted) {
      dispatchStopLoading();
    }
    if (timeoutId) clearTimeout(timeoutId);
    if (!axios.isCancel(error)) {
      if (process.env.NODE_ENV === "development") {
        console.error(`Error uploading (${method.toUpperCase()} ${url}):`, error instanceof Error ? error.message : error);
      }
    }
    throw error;
  }
};

export default ApiClient;
